/**
 * Tesoreria › Banche, movements (07/10/2026): the statement of an account in
 * the XML of CBI (the European bank-to-customer statement, camt.053, in the
 * version of the Italian banks), its movements by type, its daily balances.
 * BCC first; the same reader will do for Intesa.
 */
import { z } from 'zod';
import { parseIsoDate } from './dates.js';
import { fromCents } from './money.js';
import { normaliseIban } from './tax-ids.js';
import { isoDateSchema, requiredText } from './validation.js';
export const MOVEMENT_TYPES = [
    'entrata',
    'uscita',
    'addebito',
    'finanziamento',
    'spese',
    'altro',
];
export const MOVEMENT_TYPE_LABELS = {
    entrata: 'Bonifici in entrata',
    uscita: 'Bonifici in uscita',
    addebito: 'Addebiti (SDD, utenze, MAV)',
    finanziamento: 'Anticipi e finanziamenti',
    spese: 'Spese bancarie',
    altro: 'Altro',
};
/**
 * The type of a movement from its ABI causale (the first number of the bank
 * code, «26//11» → 26) and its direction; the description decides only for
 * the bank costs the codes leave open. To refine with use (decision of
 * 07/10/2026): the causale stays on the movement, so it can be classified again.
 */
export function movementType(abiCode, amount, description) {
    const code = (abiCode ?? '').split('/')[0].trim();
    const credit = Number(amount) > 0;
    const text = description.toUpperCase();
    // A delega is no bank cost, whatever the causale; the stamp duty is (3.23).
    if (!credit && /IMPOSTE E TASSE/.test(text) && !/SLIPG/.test(text))
        return 'addebito';
    // The causale decides; the description only for the codes it leaves open (a
    // transfer may well speak of its own fees).
    if (['16', '18', '19', '66'].includes(code))
        return 'spese';
    if (['47', '15'].includes(code))
        return 'finanziamento';
    if (code === '48')
        return 'entrata';
    if (code === '26' || code === '27')
        return credit ? 'entrata' : 'uscita';
    if (['11', '31', '10', '39', '50', '43'].includes(code))
        return credit ? 'altro' : 'addebito';
    const fees = /(COMMISSION|\bCOMM\.?\s|CANONE|IMPOSTA (DI )?BOLLO|INTERESSI|COMPETENZE|\bSPESE\b)/;
    if (!credit && fees.test(text))
        return 'spese';
    return 'altro';
}
const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };
function decode(text) {
    return text
        .replace(/&(amp|lt|gt|quot|apos);/g, (_, name) => ENTITIES[name])
        .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
        .replace(/\s+/g, ' ')
        .trim();
}
/** The text of the first element with a name below a piece of XML, or null. */
function first(xml, name) {
    const match = new RegExp(`<${name}(?:\\s[^>]*)?>([\\s\\S]*?)</${name}>`).exec(xml);
    return match ? match[1] : null;
}
function all(xml, name) {
    return [...xml.matchAll(new RegExp(`<${name}(?:\\s[^>]*)?>([\\s\\S]*?)</${name}>`, 'g'))].map((match) => match[1]);
}
function dateIn(xml) {
    const text = xml && /(\d{4}-\d{2}-\d{2})/.exec(xml)?.[1];
    if (!text)
        return null;
    try {
        parseIsoDate(text);
        return text;
    }
    catch {
        return null;
    }
}
function signedAmount(xml) {
    const amount = first(xml, 'Amt');
    const direction = first(xml, 'CdtDbtInd')?.trim();
    if (amount === null || !/^\s*\d+(\.\d{1,2})?\s*$/.test(amount))
        return null;
    const cents = Math.round(Number(amount) * 100);
    return fromCents(direction === 'DBIT' ? -cents : cents);
}
/**
 * The statement in the XML of CBI (or a plain camt.053): the IBAN, the
 * movements and the balances of each day. Namespace prefixes are ignored.
 * What cannot be read is a problem with its position, never its content.
 */
export function readCbiStatement(xml) {
    const plain = xml.replace(/<(\/?)[A-Za-z0-9_]+:/g, '<$1');
    const problems = [];
    const statements = all(plain, 'Stmt');
    if (!statements.length) {
        return {
            iban: null,
            entries: [],
            balances: [],
            problems: ['Il file non è un estratto conto XML (manca «Stmt»)'],
        };
    }
    const ibans = new Set();
    const entries = [];
    const balances = new Map();
    statements.forEach((statement, statementIndex) => {
        const iban = first(first(statement, 'Acct') ?? '', 'IBAN');
        if (iban)
            ibans.add(normaliseIban(decode(iban)));
        for (const balance of all(statement, 'Bal')) {
            const kind = first(first(balance, 'Tp') ?? '', 'Cd')?.trim();
            const date = dateIn(first(balance, 'Dt'));
            const amount = signedAmount(balance);
            if (!date || amount === null || (kind !== 'OPBD' && kind !== 'CLBD'))
                continue;
            const day = balances.get(date) ?? { date, opening: null, closing: null };
            if (kind === 'OPBD')
                day.opening = amount;
            else
                day.closing = amount;
            balances.set(date, day);
        }
        all(statement, 'Ntry').forEach((entry, entryIndex) => {
            const where = `estratto ${statementIndex + 1}, movimento ${entryIndex + 1}`;
            const status = first(entry, 'Sts')?.trim();
            if (status && status !== 'BOOK')
                return;
            const bankRef = decode(first(entry, 'AcctSvcrRef') ?? first(entry, 'NtryRef') ?? '');
            const bookingDate = dateIn(first(entry, 'BookgDt'));
            const amount = signedAmount(entry);
            if (!bankRef)
                return problems.push(`${where}: senza riferimento della banca`);
            if (!bookingDate)
                return problems.push(`${where}: senza data contabile`);
            if (amount === null)
                return problems.push(`${where}: importo non leggibile`);
            const code = first(first(entry, 'BkTxCd') ?? '', 'Cd');
            const description = all(entry, 'AddtlTxInf')
                .concat(all(entry, 'Ustrd'))
                .map(decode)
                .filter(Boolean)
                .join(' · ');
            const parties = first(entry, 'RltdPties') ?? '';
            const counterparty = first(first(parties, Number(amount) > 0 ? 'Dbtr' : 'Cdtr') ?? '', 'Nm');
            entries.push({
                bankRef: bankRef.slice(0, 80),
                bookingDate,
                valueDate: dateIn(first(entry, 'ValDt')),
                amount,
                abiCode: code ? decode(code).slice(0, 20) : null,
                description: description.slice(0, 2000) || 'Movimento senza descrizione',
                counterparty: counterparty ? decode(counterparty).slice(0, 200) : null,
            });
        });
    });
    if (ibans.size > 1)
        problems.push('Il file contiene più conti: un conto per file');
    return {
        iban: [...ibans][0] ?? null,
        entries,
        balances: [...balances.values()].sort((a, b) => a.date.localeCompare(b.date)),
        problems,
    };
}
export const loadStatementSchema = z
    .object({
    /** The XML as text. */
    xml: z.string().min(1).max(11_000_000).optional(),
    /** Or the Excel statement (sheet «Movimenti»), in base64 (1.37). */
    excel: z.string().min(1).max(16_000_000).optional(),
    /** Or several Excel files at once, e.g. the exports of Intesa (1.38). */
    files: z
        .array(z.object({
        fileName: z.string().trim().min(1).max(200),
        excel: z.string().min(1).max(2_000_000),
    }))
        .min(1)
        .max(300)
        .optional(),
    fileName: z.string().trim().min(1).max(200),
    dryRun: z.boolean().default(false),
})
    .refine((body) => [body.xml, body.excel, body.files].filter(Boolean).length === 1, {
    message: 'Serve un file: l’XML o l’Excel',
});
/** The name of the load of a movement entered by hand (1.39). */
export const MANUAL_LOAD_NAME = 'Movimento a mano';
/** A movement the bank does not export, entered by hand (1.39). */
export const manualMovementSchema = z.object({
    bookingDate: isoDateSchema,
    /** Signed: negative when it goes out. */
    amount: z
        .string()
        .trim()
        .regex(/^-?\d{1,12}([.,]\d{1,2})?$/, { message: 'Importo non valido' })
        .transform((value) => fromCents(Math.round(Number(value.replace(',', '.')) * 100)))
        .refine((value) => Number(value) !== 0, { message: 'L’importo non può essere zero' }),
    description: requiredText(300),
});
/**
 * The difference between our balance and the bank's when it is the same on
 * every day that does not agree (1.39): one movement is missing, before the
 * first of those days, rather than a whole statement. Ours minus the bank's.
 */
export function constantDifference(mismatches) {
    if (!mismatches.length)
        return null;
    const differences = new Set(mismatches.map((day) => Math.round(Number(day.ours) * 100) - Math.round(Number(day.bank) * 100)));
    return differences.size === 1 ? fromCents([...differences][0]) : null;
}
/**
 * The balance of an account and its check against the bank (07/10/2026). The
 * movements start from the balance of the account when it is the opening of
 * the first day of movements, or from an earlier day; when the bank says a
 * different opening for that first day, movements are missing in between and
 * the opening of the bank is the start. Every day the bank gives a closing
 * balance for, ours must be the same.
 */
export function bankBalanceCheck(opening, movements, bank) {
    const cents = (value) => Math.round(Number(value) * 100);
    const first = movements[0]?.bookingDate ?? null;
    const bankOn = new Map(bank.map((day) => [day.date, day]));
    let start = opening ? { ...opening, source: 'conto' } : null;
    if (first) {
        const bankOpening = bankOn.get(first)?.opening ?? null;
        const usable = opening && opening.date <= first;
        if (bankOpening !== null &&
            (!usable || (opening.date < first && cents(bankOpening) !== cents(opening.amount)))) {
            start = { amount: bankOpening, date: first, source: 'banca' };
        }
        else if (!usable) {
            start = null;
        }
    }
    let balance = start ? cents(start.amount) : 0;
    const closingOf = new Map();
    const running = movements.map((movement) => {
        if (start && movement.bookingDate >= start.date)
            balance += cents(movement.amount);
        closingOf.set(movement.bookingDate, balance);
        return fromCents(balance);
    });
    const mismatches = [];
    let checkedDays = 0;
    if (start) {
        let last = cents(start.amount);
        const days = [...new Set([...bank.map((day) => day.date), ...closingOf.keys()])].sort();
        for (const date of days) {
            if (closingOf.has(date))
                last = closingOf.get(date);
            const day = bankOn.get(date);
            if (!day?.closing || date < start.date)
                continue;
            checkedDays += 1;
            if (cents(day.closing) !== last) {
                mismatches.push({ date, ours: fromCents(last), bank: day.closing });
            }
        }
    }
    const lastMovement = movements[movements.length - 1];
    return {
        check: {
            start,
            balance: start ? fromCents(balance) : null,
            balanceDate: lastMovement?.bookingDate ?? start?.date ?? null,
            movements: movements.length,
            checkedDays,
            mismatches,
        },
        running,
    };
}
/* --- Excel statements (1.37) ---------------------------------------------- */
/** The columns of the sheet «Movimenti» of an Excel statement (BCC 2024 and 2025, 08/10/2026). */
export const EXCEL_STATEMENT_COLUMNS = [
    'id_movimento',
    'iban',
    'data_operazione',
    'data_valuta',
    'importo_netto',
    'saldo_progressivo',
    'tipo_operazione',
    'controparte',
    'descrizione_originale',
];
/**
 * The type of a movement from the «tipo operazione» of an Excel statement:
 * fees, stamp duty, account fees and charges are bank costs; advances on
 * invoices and their repayments, financing; transfers by direction; direct
 * debits, bills and taxes, debits.
 */
export function movementTypeFromOperation(operation, amount, description) {
    const text = operation.toUpperCase();
    const credit = Number(amount) > 0;
    if (/ANTICIPO|S\.?B\.?F|INTERESSI ESIGIBILI|FINANZIAMENT|MUTUO|PRESTITO/.test(text))
        return 'finanziamento';
    if (/COMMISSION|IMPOSTA|BOLLO|COMPETENZE|CANONE|\bSPESE\b/.test(text))
        return credit ? 'altro' : 'spese';
    if (/BONIFICO/.test(text))
        return credit ? 'entrata' : 'uscita';
    if (/SDD|CBILL|PAGOPA|MAV|RAV|F24|RID|UTENZ|PRELIEVO|CARTA/.test(text))
        return credit ? 'altro' : 'addebito';
    return movementType(null, amount, `${operation} ${description}`);
}
/** A date of a cell: a Date, the serial number of Excel, «AAAA-MM-GG», «GG/MM/AAAA» or «GG.MM.AAAA». */
export function cellDate(value) {
    let date = null;
    if (value instanceof Date)
        date = value;
    else if (typeof value === 'number' && value > 20000 && value < 80000)
        date = new Date(Date.UTC(1899, 11, 30) + Math.round(value) * 86_400_000);
    else if (typeof value === 'string') {
        const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
        const italian = /^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/.exec(value.trim());
        if (iso)
            date = new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])));
        else if (italian)
            date = new Date(Date.UTC(Number(italian[3]), Number(italian[2]) - 1, Number(italian[1])));
    }
    if (!date || Number.isNaN(date.getTime()))
        return null;
    // A Date of a cell is midnight local time: its calendar day is what counts.
    const day = value instanceof Date
        ? new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
        : date;
    return day.toISOString().slice(0, 10);
}
/** An amount of a cell: a number (rounded to the cent) or a text with the comma or the dot. */
export function cellCents(value) {
    if (typeof value === 'number' && Number.isFinite(value))
        return Math.round(value * 100);
    if (typeof value === 'string' && value.trim()) {
        const text = value.trim().replace(/\s/g, '');
        const normal = text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text;
        const number = Number(normal);
        return Number.isFinite(number) ? Math.round(number * 100) : null;
    }
    return null;
}
/** The text of a cell, spaces collapsed; empty for an empty cell. */
export function cellText(value) {
    return value === null || value === undefined ? '' : String(value).replace(/\s+/g, ' ').trim();
}
/**
 * The statement in the Excel layout (sheet «Movimenti», one row per movement):
 * the IBAN, the movements with their type, and the balance of each day from
 * the running balance of the rows, in their order.
 */
export function readExcelStatement(rows) {
    const problems = [];
    const missing = EXCEL_STATEMENT_COLUMNS.filter((column) => !(column in (rows[0] ?? {})));
    if (!rows.length || missing.length) {
        return {
            iban: null,
            entries: [],
            balances: [],
            problems: [
                rows.length
                    ? `Il foglio «Movimenti» non ha le colonne ${missing.join(', ')}`
                    : 'Il foglio «Movimenti» è vuoto',
            ],
        };
    }
    const ibans = new Set();
    const entries = [];
    const balances = new Map();
    rows.forEach((row, index) => {
        const where = `riga ${index + 2}`;
        const id = cellText(row.id_movimento);
        if (!id)
            return;
        const iban = cellText(row.iban);
        if (iban)
            ibans.add(normaliseIban(iban));
        const bookingDate = cellDate(row.data_operazione);
        const amount = cellCents(row.importo_netto);
        const balance = cellCents(row.saldo_progressivo);
        if (!bookingDate)
            return problems.push(`${where}: data dell’operazione non leggibile`);
        if (amount === null)
            return problems.push(`${where}: importo non leggibile`);
        const operation = cellText(row.tipo_operazione);
        const description = cellText(row.descrizione_originale) || operation || 'Movimento senza descrizione';
        entries.push({
            bankRef: `XLS:${id}`.slice(0, 80),
            bookingDate,
            valueDate: cellDate(row.data_valuta),
            amount: fromCents(amount),
            abiCode: null,
            description: description.slice(0, 2000),
            counterparty: cellText(row.controparte).slice(0, 200) || null,
            type: movementTypeFromOperation(operation, fromCents(amount), description),
        });
        if (balance !== null) {
            const day = balances.get(bookingDate) ?? {
                date: bookingDate,
                opening: fromCents(balance - amount),
                closing: null,
            };
            day.closing = fromCents(balance);
            balances.set(bookingDate, day);
        }
    });
    if (ibans.size > 1)
        problems.push('Il file contiene più conti: un conto per file');
    return {
        iban: [...ibans][0] ?? null,
        entries,
        balances: [...balances.values()].sort((a, b) => a.date.localeCompare(b.date)),
        problems,
    };
}
/* --- Intesa exports (1.38) ------------------------------------------------- */
/** A short fingerprint of a text, for the rows without a reference of the bank. */
function fingerprint(text) {
    let hash = 5381;
    for (let index = 0; index < text.length; index += 1)
        hash = ((hash << 5) + hash + text.charCodeAt(index)) | 0;
    return (hash >>> 0).toString(36);
}
/** «_Beneficiario : NURALE SRL _Motivo…» → «NURALE SRL». */
function labelled(text, label) {
    const match = new RegExp(`_${label}\\s*:\\s*(.+?)\\s*(?=_[A-Z][A-Za-z .]+\\s*:|$)`).exec(text);
    return match ? match[1].trim() || null : null;
}
/**
 * The export «movimenti.xlsx» of the portal of Intesa Sanpaolo (sheet
 * «ListaMovimenti»): at its head the account, the period, the opening and the
 * booked balance; then a row per movement with the dates, debit or credit, the
 * ABI causale and the descriptions. The reference of the bank is the
 * «Riferimento Operazione» written in the description (with the date and the
 * amount: a few rows share it); the balances of the head are used only when
 * the movements lead from the one to the other.
 */
export function readIntesaStatement(cells) {
    let iban = null;
    let from = null;
    let to = null;
    let opening = null;
    let closing = null;
    let columns = null;
    const entries = [];
    const problems = [];
    const seen = new Map();
    cells.forEach((row, index) => {
        const values = row.map((cell) => cellText(cell));
        if (!columns) {
            const label = values[0] ?? '';
            if (/^Conto Corrente/i.test(label))
                iban = normaliseIban(values.slice(1).find(Boolean) ?? '');
            else if (/^Data Contabile da/i.test(label)) {
                const dates = row
                    .slice(1)
                    .map(cellDate)
                    .filter((date) => date !== null);
                [from, to] = [dates[0] ?? null, dates[1] ?? null];
            }
            else if (/^Saldo iniziale/i.test(label))
                opening = cellCents(row.slice(1).find((cell) => cell !== null && cell !== '') ?? null);
            else if (/^Saldo contabile/i.test(label))
                closing = cellCents(row.slice(1).find((cell) => cell !== null && cell !== '') ?? null);
            else if (label === 'Data Contabile')
                columns = new Map(values.map((name, at) => [name, at]));
            return;
        }
        const at = (name) => {
            const position = columns.get(name);
            return position === undefined ? null : row[position];
        };
        const bookingDate = cellDate(at('Data Contabile'));
        if (!bookingDate) {
            if (values.some(Boolean))
                problems.push(`riga ${index + 1}: data contabile non leggibile`);
            return;
        }
        const debit = cellCents(at('Dare')) ?? 0;
        const credit = cellCents(at('Avere')) ?? 0;
        const cents = Math.abs(credit) - Math.abs(debit);
        const extra = `${cellText(at('Descrizioni Aggiuntive'))}${cellText(at('Descrizioni Aggiuntive - Seconda parte'))}`;
        const causale = cellText(at('Descrizione Causale ABI/SWIFT'));
        const description = [causale, extra].filter(Boolean).join(' · ').replace(/\s+/g, ' ').trim();
        const reference = /Riferimento Operazione\s*:\s*([A-Z0-9]+)/.exec(extra)?.[1] ?? fingerprint(description);
        const key = `INT:${reference}:${bookingDate}:${cents}`;
        const occurrence = (seen.get(key) ?? 0) + 1;
        seen.set(key, occurrence);
        const abiCode = cellText(at('Causale ABI/Swift')) || null;
        const counterparty = cents > 0
            ? labelled(extra, 'Ordinante')
            : (labelled(extra, 'Beneficiario') ?? labelled(extra, 'Creditore'));
        entries.push({
            bankRef: (occurrence > 1 ? `${key}#${occurrence}` : key).slice(0, 80),
            bookingDate,
            valueDate: cellDate(at('Data Valuta')),
            amount: fromCents(cents),
            abiCode: abiCode ? abiCode.slice(0, 20) : null,
            description: description.slice(0, 2000) || 'Movimento senza descrizione',
            counterparty: counterparty ? counterparty.slice(0, 200) : null,
        });
    });
    if (!columns)
        problems.push('Il file non è un export dei movimenti (manca la riga «Data Contabile»)');
    if (opening === null || closing === null)
        problems.push('Mancano il saldo iniziale o il saldo contabile in testa al file');
    if (problems.length || opening === null || closing === null) {
        return { iban, entries: [], balances: [], problems };
    }
    const total = entries.reduce((sum, entry) => sum + Math.round(Number(entry.amount) * 100), 0);
    const agrees = opening + total === closing;
    const days = entries.map((entry) => entry.bookingDate).sort();
    const balances = [];
    if (agrees) {
        const first = days[0] ?? from;
        if (first)
            balances.push({ date: first, opening: fromCents(opening), closing: null });
        const last = to ?? days[days.length - 1];
        if (last) {
            const same = balances.find((day) => day.date === last);
            if (same)
                same.closing = fromCents(closing);
            else
                balances.push({ date: last, opening: null, closing: fromCents(closing) });
        }
    }
    return {
        iban,
        entries,
        balances,
        problems,
        header: { from, to, opening: fromCents(opening), closing: fromCents(closing), agrees },
    };
}
//# sourceMappingURL=bank-statements.js.map